/**
 * Cascading dropdown property type — a dependent select up to three levels deep, the one
 * property type missing from the plain text/date/select/multi-select/number/file set.
 *
 * Stored as a flat list of full paths (`["Electronics","Phones","Android"]`) rather than a
 * nested tree: a flat list is trivial to validate for duplicates and depth, and building the
 * actual cascading `<select>` chain from it is a matter of grouping by prefix at render time,
 * not something that needs the storage shape to already be a tree.
 */

export const MAX_CASCADE_DEPTH = 3

/** Returns a human-readable problem, or null if the option set is usable. */
export function validateCascadeOptions(paths: string[][]): string | null {
  if (paths.length === 0) return 'A cascading field needs at least one option path.'

  const seen = new Set<string>()
  for (const path of paths) {
    if (path.length === 0) return 'Every option path needs at least one level.'
    if (path.length > MAX_CASCADE_DEPTH) {
      return `A cascading field can only go ${MAX_CASCADE_DEPTH} levels deep.`
    }
    const key = path.join(' > ')
    if (seen.has(key)) return `"${key}" is listed more than once.`
    seen.add(key)
  }
  return null
}

export interface CascadeNode {
  label: string
  path: string[]
  children: CascadeNode[]
}

/** Groups flat paths into a tree, for a UI that renders one `<select>` per level. */
export function cascadeTree(paths: string[][]): CascadeNode[] {
  const roots: CascadeNode[] = []
  for (const path of paths) {
    let level = roots
    let soFar: string[] = []
    for (const label of path) {
      soFar = [...soFar, label]
      let node = level.find((n) => n.label === label)
      if (!node) {
        node = { label, path: soFar, children: [] }
        level.push(node)
      }
      level = node.children
    }
  }
  return roots
}

/** The options available at one level, given the choices already made above it. */
export function cascadeOptionsAt(paths: string[][], chosen: string[]): string[] {
  const tree = cascadeTree(paths)
  let level = tree
  for (const label of chosen) {
    const node = level.find((n) => n.label === label)
    if (!node) return []
    level = node.children
  }
  return level.map((n) => n.label)
}

/**
 * Named, reorderable property sections.
 *
 * `property_defs.section` has always been a free-text string per field, with no first-class
 * ordering of the sections themselves — `property_sections` names and orders them explicitly,
 * but a workspace that never opens that screen still needs its existing grouping to render in
 * a stable, sensible order. A section nobody has ever explicitly ordered sorts after every
 * named one, in the order it was first seen in `defs` — so nothing breaks the first time this
 * shipped, and nothing breaks for a workspace that still never touches it.
 */
export function orderPropertyDefs<D extends { entity: string; section: string; displayOrder: number }>(
  defs: D[],
  sections: { entity: string; name: string; displayOrder: number }[],
): D[] {
  const orderByName = new Map(sections.map((s) => [`${s.entity}:${s.name}`, s.displayOrder]))
  let nextFallback = sections.length ? Math.max(...sections.map((s) => s.displayOrder)) + 1 : 0
  const fallbackOrder = new Map<string, number>()
  for (const def of defs) {
    const key = `${def.entity}:${def.section}`
    if (orderByName.has(key) || fallbackOrder.has(key)) continue
    fallbackOrder.set(key, nextFallback++)
  }
  const sectionOrder = (entity: string, section: string) =>
    orderByName.get(`${entity}:${section}`) ?? fallbackOrder.get(`${entity}:${section}`) ?? 0

  return [...defs].sort((a, b) => {
    const bySection = sectionOrder(a.entity, a.section) - sectionOrder(b.entity, b.section)
    return bySection !== 0 ? bySection : a.displayOrder - b.displayOrder
  })
}
